import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import {
  Box,
  Button,
  Fab,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { Navigate, useParams } from "react-router-dom";
import {
  MediaItemSource,
  ResponseOfMediaItem,
} from "services/KnowfullyAdminClient";
import { Severity, SnackbarMessageProps } from "types/common-types";
import MediaLibraryItemPreview from "./MediaLibraryItemPreview";
import MediaLibraryItemUpdateForm from "./MediaLibraryItemUpdateForm";
import useTitle from "hooks/useTitle";

const MediaLibraryItemDetails = () => {
  const { registerPromise } = useTrashablePromises();
  const { id } = useParams<{ id: string }>();
  const { mediaLibraryUrl, mediaService } = useGlobalContext();
  const [updateOpen, updateOpenActions] = useBoolean(false);
  const { formatMessage: t } = useIntl();

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value);
    setSnackbarMessage(
      new SnackbarMessageProps(
        t({ id: "copiedToClipboardMessage" }),
        Severity.Success
      )
    );
  };

  const { isLoading, isError, data } = useQuery<ResponseOfMediaItem>(
    ["media-item", id],
    () =>
      registerPromise(mediaService.getItem(id!, MediaItemSource.MediaLibrary)),
    {
      enabled: !!id,
    }
  );

  const mediaLibraryItem = data?.data;
  useTitle(mediaLibraryItem?.fileName || "Media Library Item Details");

  if (!id) return <Navigate to={mediaLibraryUrl()} replace />;
  if (isLoading) return <Loader />;
  if (isError) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="mediaLibraryItemLoadError" />
        </Alert>
      </Box>
    );
  }

  const handleMediaLibraryItemUpdated = () => {
    updateOpenActions.setFalse();
    setSnackbarMessage(
      new SnackbarMessageProps(
        t({ id: "mediaLibraryItemUpdated" }),
        Severity.Success
      )
    );
  };

  if (updateOpen) {
    return (
      <>
        <Box my={2}>
          <Button
            color="primary"
            onClick={updateOpenActions.setFalse}
            startIcon={<ArrowBackIcon />}
          >
            <T id="backToMediaLibraryItemLabel" />
          </Button>
        </Box>
        <MediaLibraryItemUpdateForm
          mediaItem={mediaLibraryItem!}
          onMediaLibraryItemUpdated={handleMediaLibraryItemUpdated}
          onCancel={updateOpenActions.setFalse}
        />
      </>
    );
  }

  const {
    fileUrl,
    organizationName,
    alternativeText,
    description,
  } = mediaLibraryItem!;

  const rows = [
    {
      title: t({ id: "urlLabel" }),
      component: (
        <>
          <span>{fileUrl}</span>
          <IconButton
            style={{ marginLeft: "10px" }}
            size="small"
            onClick={() => copyToClipboard(fileUrl!)}
          >
            <FileCopyIcon />
          </IconButton>
        </>
      ),
    },
    {
      title: t({ id: "organization" }),
      component: <span> {organizationName}</span>,
    },
    {
      title: t({ id: "alternativeTextLabel" }),
      component: <span>{alternativeText}</span>,
    },
    {
      title: t({ id: "descriptionLabel" }),
      component: <span>{description}</span>,
    },
  ].filter((r) => !!r.component);

  return (
    <>
      <Box my={2}>
        <RedirectBackButton
          redirectRoute={mediaLibraryUrl()}
          labelKey="backToListButtonLabel"
        />
      </Box>

      <Box my={3} display="flex" justifyContent="center" gap={2}>
        <Fab color="primary" onClick={updateOpenActions.setTrue}>
          <EditIcon />
        </Fab>
        {!!mediaLibraryItem?.originalFileUrl && (
          <Tooltip title="Download Original">
            <Fab
              color="primary"
              component="a"
              href={mediaLibraryItem?.originalFileUrl}
              target="_blank"
              download
            >
              <DownloadIcon />
            </Fab>
          </Tooltip>
        )}
      </Box>

      <Box my={5}>
        <MediaLibraryItemPreview
          fileName={mediaLibraryItem?.fileName!}
          fileSize={mediaLibraryItem?.fileSize!}
          contentType={mediaLibraryItem?.contentType!}
          dimensions={mediaLibraryItem?.dimensions}
          imageSrc={mediaLibraryItem?.fileUrl}
          uploadedBy={mediaLibraryItem?.createdBy}
          uploadedOn={mediaLibraryItem?.createdOn}
        />
      </Box>

      <Box my={3}>
        <TableContainer>
          <Table>
            <TableBody>
              {rows.map(({ title, component }, i) => (
                <TableRow key={i}>
                  <TableCell component="th" scope="row">
                    <b>{title}</b>
                  </TableCell>
                  <TableCell>{component}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default MediaLibraryItemDetails;
